import React, { useEffect, useRef } from 'react'
import { useReportInPlaceExpandOptional } from '@components/pages/Reports/shared/ReportInPlaceExpandContext'

type ReportExpandableSectionProps = {
  sectionId: string
  className?: string
  children: React.ReactNode
}

/**
 * Splits a report into hideable blocks. When one block is in “full view”, others unmount.
 * Must sit under {@link ReportInPlaceExpandProvider} (Reports bundle) — otherwise renders a plain div.
 */
function ReportExpandableSection(
  props: ReportExpandableSectionProps,
): React.ReactElement {
  const { sectionId, className = '', children } = props
  const ctx = useReportInPlaceExpandOptional()
  const ref = useRef<HTMLDivElement>(null)
  const expanded = ctx?.expandedSectionId
  const isHidden = expanded != null && expanded !== sectionId
  const isThisExpanded = expanded === sectionId

  useEffect(() => {
    if (isThisExpanded) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [isThisExpanded])

  if (ctx == null) {
    return className ? (
      <div className={className}>{children}</div>
    ) : (
      <>{children}</>
    )
  }

  if (isHidden) {
    return <></>
  }

  return (
    <div
      ref={ref}
      className={[
        'report-expandable-section',
        isThisExpanded ? 'report-expandable-section--expanded' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  )
}

export default ReportExpandableSection
